// ui/src/components/tenant-admin/sections/RateTableReference.jsx

/**
 * @file RateTableReference.jsx
 * @description Plan document section for the governed rate table reference.
 *   Displays the pinned rate table, curve preview, and version upgrade path.
 *   Fetches version comparison data from ep 33
 *   (GET /api/v1/rate-tables/{id}/compare) when the comparison panel is opened.
 *   Initiates Change Set via ep 34 (POST /api/v1/rate-tables/change-sets)
 *   through the onStartChangeSet callback. Rate table selection uses
 *   RateTableLibraryDrawer (ep 32).
 *
 *   S902 — OI-S899-RATE-TABLE-CHANGE-UI-GAP FIXED:
 *   Empty state is now count-aware (EP32 totalCount on mount):
 *     0 library tables → inline 4-step default creator → EP66
 *     1 library table  → show + offer clone or create new → EP66
 *     2+ library tables → library drawer picker → EP66 (clone path)
 *   Draft plans route onSelect through EP66
 *   (POST /api/v1/plans/{planId}/components/{planComponentId}/rate-table).
 *   Approved plans retain EP34 change-set path unchanged.
 *   planComponentId prop added (required for EP66).
 *
 * @module tenant-admin/sections
 * @author data-fit LLC · DecisionFlow · S775 / S902
 * @see api_spec_fileB_v4_final.md §2.5 — rate-tables/{id}/compare (ep 33)
 * @see api_spec_fileB_v4_final.md §2.6 — rate-tables/change-sets (ep 34)
 * @see api_spec_fileB_v4_final.md §2.4 — rate-tables list (ep 32)
 * @see EP66 POST /api/v1/plans/{id}/components/{id}/rate-table (S902)
 */

import React, { useCallback, useEffect, useState } from "react";
import RateTableLibraryDrawer from "./RateTableLibraryDrawer";

// S812 D2 — effectiveDateCompatibility badge labels.
// Badge shows NOTHING before a rate table is selected (pre-selection = no field).
// Badge appears ONLY after selection, derived from effectiveDateCompatibility from EP 33.
// Confirmed: rate_table.max_deal_commission numeric(18,4) — r60 ✅ (deal-level cap field)
const COMPAT_LABELS = {
  compatible: { label: 'Compatible with this plan',                                               color: 'modifier-manager-badge--complete'    },
  gap:        { label: 'Date gap — this rate table does not cover the full plan period',           color: 'modifier-manager-badge--warning'     },
  overlap:    { label: 'Date overlap — this rate table conflicts with plan effective dates',       color: 'modifier-manager-badge--incomplete'  },
};

function normalizeComparison(payload) {
  return {
    differences:
      payload.differences ??
      payload.tier_threshold_differences ??
      [],
    estimatedEconomicImpact:
      payload.estimatedEconomicImpact ??
      payload.estimated_economic_impact ??
      null,
    affectedPositionCount:
      payload.affectedPositionCount ??
      payload.affected_position_count ??
      null,
    effectiveDateCompatibility:
      payload.effectiveDateCompatibility ??
      payload.effective_date_compatibility ??
      null,
    spineCompatibility:
      payload.spineCompatibility ??
      payload.spine_compatibility ??
      null,
  };
}

function effectiveScope(ref) {
  if (!ref.effectiveFrom && !ref.effectiveTo) return "Not specified";
  if (ref.effectiveFrom && !ref.effectiveTo) {
    return `${ref.effectiveFrom} onward`;
  }
  if (!ref.effectiveFrom && ref.effectiveTo) {
    return `Through ${ref.effectiveTo}`;
  }
  return `${ref.effectiveFrom} – ${ref.effectiveTo}`;
}

// S902 — default 4-step seed tiers displayed in the inline creator (OD-S902-B).
// Mirrors _DEFAULT_RATE_TABLE_TIERS in plan_authoring_router.py EP66.
const DEFAULT_TIERS = [
  { fromPct: "0",   toPct: "50",  payoutPct: "0",   isAccelerator: false, interpolation: "step" },
  { fromPct: "50",  toPct: "100", payoutPct: "100", isAccelerator: false, interpolation: "step" },
  { fromPct: "100", toPct: "150", payoutPct: "125", isAccelerator: true,  interpolation: "step" },
  { fromPct: "150", toPct: "",    payoutPct: "150", isAccelerator: true,  interpolation: "step" },
];

// S902 — call EP66 to create or clone a rate table on a draft plan component.
// Returns { rateTableId, planComponentId, tierCount, rateTableCode, rateTableName }.
async function callEP66(planId, componentId, body, signal) {
  const response = await fetch(
    `/api/v1/plans/${encodeURIComponent(planId)}/components/${encodeURIComponent(componentId)}/rate-table`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `EP66 failed (${response.status}).`);
  }
  return response.json();
}

// S902 — shared input style for inline rate table builder inputs.
// Uses explicit CSS variable tokens matching .modifier-manager-percent-input input
// from index.css (lines 482-492). Bare inputs without explicit background/color
// fall through to browser UA stylesheet and render dark in some themes.
// Width set to 100% — cells constrain the actual rendered width via table layout.
const _tierInputStyle = {
  width: "100%",
  padding: "4px 8px",
  border: "1px solid rgb(var(--color-border))",
  borderRadius: 6,
  background: "rgb(var(--color-surface))",
  color: "rgb(var(--color-text))",
  fontSize: 12,
  transition: "border-color 0.15s",
  boxSizing: "border-box",
};

// S902 — InlineRateTableBuilder: shown when 0 library tables exist, or when
// the user opts to create a new table from scratch instead of picking from library.
// tiers state is editable rows; onSave fires callEP66 create path.
function InlineRateTableBuilder({ planId, planComponentId, onSaved, onCancel, hybridSlot = null }) {
  const [tiers, setTiers]         = useState(DEFAULT_TIERS.map((t) => ({ ...t })));
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState("");

  function updateTier(index, field, value) {
    setTiers((prev) =>
      prev.map((t, i) => (i === index ? { ...t, [field]: value } : t))
    );
  }

  function addStep() {
    const last = tiers[tiers.length - 1];
    setTiers((prev) => [
      ...prev,
      {
        fromPct:       last?.toPct || "",
        toPct:         "",
        payoutPct:     "",
        isAccelerator: false,
        interpolation: "step",
      },
    ]);
  }

  function removeStep(index) {
    if (tiers.length <= 1) return;
    setTiers((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSave() {
    setSaving(true);
    setSaveError("");
    try {
      const mappedTiers = tiers.map((t) => ({
        fromPct:       parseFloat(t.fromPct) / 100,
        toPct:         t.toPct !== "" ? parseFloat(t.toPct) / 100 : null,
        payoutPct:     parseFloat(t.payoutPct) / 100,
        isAccelerator: Boolean(t.isAccelerator),
        interpolation: t.interpolation || "step",
      }));
      const result = await callEP66(planId, planComponentId, {
        tiers: mappedTiers,
        ...(hybridSlot ? { hybridSlot } : {}),
      });
      onSaved(result);
    } catch (err) {
      setSaveError(err.message || "Failed to save rate table.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modifier-manager-body">
      <p style={{ marginTop: 0, fontSize: "0.875rem", color: "rgb(var(--color-text-muted))" }}>
        Define the commission steps for this plan. You can adjust these at any time before submission.
      </p>

      <div className="modifier-manager-table-wrapper">
        <table className="modifier-manager-table" style={{ width: "100%", borderCollapse: "collapse" }}>
          <caption className="sr-only">Rate table steps</caption>
          <thead>
            <tr style={{ fontSize: "0.75rem", textTransform: "uppercase",
                         letterSpacing: "0.05em", color: "rgb(var(--color-text-muted))" }}>
              <th scope="col" style={{ padding: "6px 8px", textAlign: "left", fontWeight: 600 }}>From (%)</th>
              <th scope="col" style={{ padding: "6px 8px", textAlign: "left", fontWeight: 600 }}>To (% — blank = no ceiling)</th>
              <th scope="col" style={{ padding: "6px 8px", textAlign: "left", fontWeight: 600 }}>Rate (%)</th>
              <th scope="col" style={{ padding: "6px 8px", textAlign: "center", fontWeight: 600 }}>Accelerator</th>
              <th scope="col" style={{ padding: "6px 8px" }}><span className="sr-only">Remove</span></th>
            </tr>
          </thead>
          <tbody>
            {tiers.map((tier, index) => (
              <tr key={index} style={{ borderTop: "1px solid rgb(var(--color-border))" }}>
                <td style={{ padding: "6px 8px" }}>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={tier.fromPct}
                    onChange={(e) => updateTier(index, "fromPct", e.target.value)}
                    style={_tierInputStyle}
                    aria-label={`Step ${index + 1} from percentage`}
                  />
                </td>
                <td style={{ padding: "6px 8px" }}>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={tier.toPct}
                    placeholder="No ceiling"
                    onChange={(e) => updateTier(index, "toPct", e.target.value)}
                    style={_tierInputStyle}
                    aria-label={`Step ${index + 1} to percentage`}
                  />
                </td>
                <td style={{ padding: "6px 8px" }}>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={tier.payoutPct}
                    onChange={(e) => updateTier(index, "payoutPct", e.target.value)}
                    style={_tierInputStyle}
                    aria-label={`Step ${index + 1} payout percentage`}
                  />
                </td>
                <td style={{ padding: "6px 8px", textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={Boolean(tier.isAccelerator)}
                    onChange={(e) => updateTier(index, "isAccelerator", e.target.checked)}
                    aria-label={`Step ${index + 1} is accelerator`}
                  />
                </td>
                <td style={{ padding: "6px 8px" }}>
                  <button
                    type="button"
                    className="text-button"
                    onClick={() => removeStep(index)}
                    disabled={tiers.length <= 1}
                    aria-label={`Remove step ${index + 1}`}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button type="button" className="text-button" onClick={addStep}
        style={{ marginTop: "10px", fontSize: "0.875rem" }}>
        + Add step
      </button>

      {saveError && (
        <div className="modifier-manager-inline-error" role="alert" style={{ marginTop: "8px" }}>
          {saveError}
        </div>
      )}

      <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
        <button
          type="button"
          className="primary-button"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save rate table"}
        </button>
        {onCancel && (
          <button type="button" className="secondary-button" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

// S902 — SingleTablePrompt: shown when exactly 1 library table exists.
// Offers "Use this one" (clone via EP66) or "Create new instead" (inline builder).
function SingleTablePrompt({ libraryTable, planId, planComponentId, onSaved, onCreateNew, hybridSlot = null }) {
  const [cloning, setCloning]     = useState(false);
  const [cloneError, setCloneError] = useState("");

  async function handleUseThis() {
    setCloning(true);
    setCloneError("");
    try {
      const result = await callEP66(planId, planComponentId, {
        sourceRateTableId: libraryTable.rateTableId,
        ...(hybridSlot ? { hybridSlot } : {}),
      });
      onSaved(result);
    } catch (err) {
      setCloneError(err.message || "Failed to attach rate table.");
    } finally {
      setCloning(false);
    }
  }

  return (
    <div className="modifier-manager-body">
      <p style={{ marginTop: 0 }}>
        One rate table is available in your library:
      </p>
      <div className="modifier-manager-table-wrapper" style={{ marginBottom: "12px" }}>
        <table className="modifier-manager-table">
          <caption className="sr-only">Available rate table</caption>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Version</th>
              <th scope="col">Tiers</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{libraryTable.rateTableName}</td>
              <td>V{libraryTable.version}</td>
              <td>{libraryTable.tierCount}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {cloneError && (
        <div className="modifier-manager-inline-error" role="alert" style={{ marginBottom: "8px" }}>
          {cloneError}
        </div>
      )}

      <div style={{ display: "flex", gap: "8px" }}>
        <button
          type="button"
          className="primary-button"
          onClick={handleUseThis}
          disabled={cloning}
        >
          {cloning ? "Attaching…" : "Use this rate table"}
        </button>
        <button
          type="button"
          className="secondary-button"
          onClick={onCreateNew}
          disabled={cloning}
        >
          Create a new one instead
        </button>
      </div>
    </div>
  );
}

export default function RateTableReference({
  rateTableRef,
  planId,
  planComponentId,
  planSpine,
  planStatus,
  readOnly,
  onSelect,
  onVersionUpdate,
  onStartAmendment,
  onStartChangeSet,
  // S996 OI-HYBRID-RATE-TABLE-ID-NO-WRITE-PATH:
  // label overrides the default "Rate table" heading — used when instantiated
  // multiple times in the hybrid card to distinguish own-production vs team-rollup
  // vs plan-level fallback. contextNote renders a muted guidance line below the heading.
  label       = "Rate table",
  contextNote = null,
  hybridSlot  = null,   // "ic" | "rollup" | null — forwarded to EP66 for atomic write-back
}) {
  const [drawerOpen, setDrawerOpen]               = useState(false);
  const [comparisonOpen, setComparisonOpen]       = useState(false);
  const [comparisonData, setComparisonData]       = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonError, setComparisonError]     = useState("");
  const [newerVersionDismissed, setNewerVersionDismissed] = useState(false);

  // S902 — empty state: library count fetch + mode tracking.
  const [libraryCount, setLibraryCount]   = useState(null);   // null = loading
  const [libraryFirst, setLibraryFirst]   = useState(null);   // first table when count=1
  const [libraryError, setLibraryError]   = useState("");
  // emptyMode: "loading" | "zero" | "one" | "many" | "building"
  // "building" = user chose to create from scratch (from one or many path)
  const [emptyMode, setEmptyMode]         = useState("loading");

  const approvedWorkflow = planStatus === "approved";
  const isDraft          = planStatus === "draft";
  const targetVersion    = rateTableRef?.newerVersionLabel ?? null;

  // S902 — fetch EP32 library count on mount when no rate table is attached.
  // Determines which empty-state branch to show (zero/one/many).
  // Uses AbortController for cleanup — no ref guard needed. On remount the
  // effect re-fires correctly because plan.planId is in the dependency array.
  // Previously used countFetchedRef which reset on remount, causing the fetch
  // to be cancelled by the parent re-render and emptyMode to stick at "loading".
  useEffect(() => {
    if (rateTableRef?.rateTableId) return;   // already has a rate table — no fetch needed
    if (!isDraft)                  return;   // approved path doesn't use count-aware state
    if (readOnly)                  return;
    if (!planId)                   return;   // planId not yet available — wait for next render

    const controller = new AbortController();

    async function fetchLibraryCount() {
      setEmptyMode("loading");
      setLibraryError("");
      try {
        const resp = await fetch(
          "/api/v1/rate-tables?page_size=2&is_active=true",
          { signal: controller.signal }
        );
        if (!resp.ok) throw new Error(`EP32 failed (${resp.status}).`);
        const data = await resp.json();
        const count = data.totalCount ?? data.rateTables?.length ?? data.items?.length ?? 0;
        setLibraryCount(count);
        if (count === 0) {
          setEmptyMode("zero");
        } else if (count === 1) {
          const first = (data.rateTables ?? data.items ?? [])[0] ?? null;
          setLibraryFirst(first);
          setEmptyMode("one");
        } else {
          setEmptyMode("many");
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          setLibraryError("Unable to load rate table library.");
          setEmptyMode("zero");  // graceful fallback: show inline builder
        }
        // AbortError = component unmounted mid-fetch — do nothing, state will
        // be re-established on next mount via this same effect.
      }
    }

    fetchLibraryCount();
    return () => controller.abort();
  // Re-fire when planId becomes available (parent re-render after doc load)
  // or when rateTableRef clears (unlikely but safe to handle).
  }, [rateTableRef?.rateTableId, isDraft, readOnly, planId]); // eslint-disable-line react-hooks/exhaustive-deps

  // S902 — after EP66 succeeds, normalise the response into the shape
  // that onSelect expects (matching EP32 rateTable object shape),
  // then call onSelect so the parent refetches and re-renders the populated state.
  const handleEP66Saved = useCallback((ep66Result) => {
    onSelect({
      rateTableId:   ep66Result.rateTableId,
      rateTableName: ep66Result.rateTableName,
      rateTableCode: ep66Result.rateTableCode,
      tierCount:     ep66Result.tierCount,
      versionLabel:  "V1",
      bands:         [],   // parent refetch will populate bands
    });
  }, [onSelect]);

  // S902 — when the user picks from the library drawer (many path),
  // clone via EP66 rather than calling onSelect directly (which previously
  // hit EP34 for draft plans). For approved plans, onSelect goes to parent unchanged.
  const handleDrawerSelect = useCallback(async (ref) => {
    setDrawerOpen(false);
    if (isDraft && planId && planComponentId) {
      // Clone path via EP66.
      try {
        const result = await callEP66(planId, planComponentId, {
          sourceRateTableId: ref.rateTableId,
          ...(hybridSlot ? { hybridSlot } : {}),
        });
        handleEP66Saved(result);
      } catch (err) {
        // Surface error — re-open drawer so user isn't left stranded.
        setDrawerOpen(true);
      }
    } else {
      // Approved workflow — parent handles via EP34 change-set path.
      onSelect(ref);
    }
  }, [isDraft, planId, planComponentId, handleEP66Saved, onSelect]);

  useEffect(() => {
    setComparisonOpen(false);
    setComparisonData(null);
    setComparisonError("");
    setNewerVersionDismissed(false);
  }, [rateTableRef?.rateTableId, targetVersion]);

  useEffect(() => {
    if (
      !comparisonOpen ||
      !rateTableRef?.rateTableId ||
      !targetVersion ||
      comparisonData
    ) {
      return undefined;
    }

    const controller = new AbortController();

    async function loadComparison() {
      setComparisonLoading(true);
      setComparisonError("");

      try {
        // ep 33 — GET /api/v1/rate-tables/{id}/compare
        // Router: plan_governance_router.py
        // Response: sourceRateTableId, targetRateTableId, tierChanges[],
        //           curveTypeChanged, affectedPlans[]
        // Note: normalizeComparison handles both camelCase and snake_case
        //       keys to accommodate any router/spec drift.
        const response = await fetch(
          `/api/v1/rate-tables/${encodeURIComponent(
            rateTableRef.rateTableId
          )}/compare?target_version=${encodeURIComponent(targetVersion)}`,
          { signal: controller.signal }
        );

        if (!response.ok) {
          throw new Error(
            `Unable to compare versions (${response.status}).`
          );
        }

        setComparisonData(normalizeComparison(await response.json()));
      } catch (error) {
        if (error.name !== "AbortError") {
          setComparisonError(
            error.message || "Unable to compare rate-table versions."
          );
        }
      } finally {
        if (!controller.signal.aborted) {
          setComparisonLoading(false);
        }
      }
    }

    loadComparison();
    return () => controller.abort();
  }, [
    comparisonData,
    comparisonOpen,
    rateTableRef?.rateTableId,
    targetVersion,
  ]);

  // S902 — count-aware empty state (draft only).
  if (!rateTableRef?.rateTableId) {
    return (
      <>
        <section className="modifier-manager-card">
          <header className="modifier-manager-header">
            <h2>{label}</h2>
            {contextNote && (
              <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: 'rgb(var(--color-text-muted))' }}>
                {contextNote}
              </p>
            )}
          </header>

          {readOnly ? (
            <div className="modifier-manager-empty-state">
              <p>No rate table selected.</p>
            </div>
          ) : emptyMode === "loading" ? (
            <div className="modifier-manager-body" aria-busy="true" aria-label="Loading rate table options">
              <div className="skeleton" style={{ height: 36, borderRadius: 6 }} />
              <div className="skeleton" style={{ height: 36, borderRadius: 6, width: "70%" }} />
            </div>
          ) : emptyMode === "zero" || emptyMode === "building" ? (
            // 0 tables in library, or user chose to build from scratch.
            <>
              {emptyMode === "zero" && (
                <div className="modifier-manager-body" style={{ paddingBottom: 0 }}>
                  <p style={{ margin: "0 0 4px", fontSize: "0.875rem",
                               color: "rgb(var(--color-text-muted))" }}>
                    No rate tables exist yet. Set up the commission tiers for this plan below.
                  </p>
                  {/* libraryError suppressed in zero mode — EP32 failure is not
                      actionable here since the inline builder is the correct path. */}
                </div>
              )}
              <InlineRateTableBuilder
                planId={planId}
                planComponentId={planComponentId}
                onSaved={handleEP66Saved}
                onCancel={emptyMode === "building" ? () => setEmptyMode(
                  libraryFirst ? "one" : "many"
                ) : null}
                hybridSlot={hybridSlot}
              />
            </>
          ) : emptyMode === "one" ? (
            // Exactly 1 library table — show it and offer choice.
            <SingleTablePrompt
              libraryTable={libraryFirst}
              planId={planId}
              planComponentId={planComponentId}
              onSaved={handleEP66Saved}
              onCreateNew={() => setEmptyMode("building")}
              hybridSlot={hybridSlot}
            />
          ) : (
            // 2+ library tables — library drawer picker.
            <div className="modifier-manager-empty-state">
              <p>Choose a rate table from your library, or create a new one.</p>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  type="button"
                  className="primary-button"
                  onClick={() => setDrawerOpen(true)}
                >
                  Choose from library
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setEmptyMode("building")}
                >
                  Create new
                </button>
              </div>
            </div>
          )}
        </section>

        <RateTableLibraryDrawer
          open={drawerOpen}
          currentRef={null}
          planSpine={planSpine}
          onSelect={handleDrawerSelect}
          onClose={() => setDrawerOpen(false)}
        />
      </>
    );
  }

  const comparisonViewed = Boolean(comparisonData);

  return (
    <>
      <section className="modifier-manager-card">
        <header className="modifier-manager-header">
          <div>
            <h2 style={{ margin: 0 }}>{label}</h2>
            {contextNote && (
              <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: 'rgb(var(--color-text-muted))' }}>
                {contextNote}
              </p>
            )}
            {/* Compact single-line summary: name · version · scope · deal cap */}
            <p style={{ margin: '2px 0 0', fontSize: '0.875rem', color: 'rgb(var(--color-text-muted))' }}>
              <strong style={{ color: 'rgb(var(--color-text))' }}>{rateTableRef.rateTableName}</strong>
              {' · '}{rateTableRef.versionLabel}
              {' · '}{effectiveScope(rateTableRef)}
              {/* S812: rate_table.max_deal_commission numeric(18,4) [r60 confirmed]
                  Deal cap: per-credit ceiling applied before period cap (Layer 2). Read-only. */}
              {' · '}
              {rateTableRef.maxDealCommission != null
                ? `Cap ${rateTableRef.maxDealCommission}${rateTableRef.currencyCode ? ' ' + rateTableRef.currencyCode : ''}`
                : 'No deal cap'}
            </p>
          </div>
          {/* S812 D2: Badge only after selection when effectiveDateCompatibility is non-null */}
          {rateTableRef.effectiveDateCompatibility &&
            COMPAT_LABELS[rateTableRef.effectiveDateCompatibility] && (
            <span className={COMPAT_LABELS[rateTableRef.effectiveDateCompatibility].color}>
              {COMPAT_LABELS[rateTableRef.effectiveDateCompatibility].label}
            </span>
          )}
        </header>

        <div className="modifier-manager-body">
          {rateTableRef.usedInFinalRun && (
            <div className="modifier-manager-readonly-note">
              This version has been used in a calculation run and cannot
              be modified. Create a new version to make changes.
            </div>
          )}

          <div className="modifier-manager-table-wrapper">
            <table className="modifier-manager-table">
              <caption className="sr-only">
                Rate table curve preview
              </caption>
              <thead>
                <tr>
                  <th scope="col">From</th>
                  <th scope="col">To</th>
                  <th scope="col">Rate</th>
                </tr>
              </thead>
              <tbody>
                {(rateTableRef.bands ?? []).map((band, index) => (
                  <tr key={`${band.from}:${band.to}:${index}`}>
                    <td>{band.from}%</td>
                    <td>{band.to == null ? "No ceiling" : `${band.to}%`}</td>
                    <td>{band.rate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {rateTableRef.hasNewerVersion &&
            targetVersion &&
            !newerVersionDismissed && (
              <div className="modifier-manager-info">
                <p>
                  <strong>Version {targetVersion} is available</strong>
                  {rateTableRef.newerVersionSummary
                    ? ` — ${rateTableRef.newerVersionSummary}`
                    : ""}
                </p>
                <div>
                  <button
                    type="button"
                    className="secondary-button"
                    aria-expanded={comparisonOpen}
                    onClick={() => setComparisonOpen((value) => !value)}
                  >
                    Compare versions
                  </button>{" "}
                  <button
                    type="button"
                    className="text-button"
                    onClick={() => setNewerVersionDismissed(true)}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}

          {comparisonOpen && (
            <section
              className="modifier-manager-card"
              aria-label={`Comparison with ${targetVersion}`}
            >
              <header className="modifier-manager-header">
                <h3>
                  {rateTableRef.versionLabel} → {targetVersion}
                </h3>
              </header>

              <div className="modifier-manager-body">
                {comparisonLoading && (
                  <div aria-busy="true">
                    <div className="skeleton" style={{ height: 72 }} />
                  </div>
                )}

                {comparisonError && (
                  <div
                    className="modifier-manager-inline-error"
                    role="alert"
                  >
                    {comparisonError}
                  </div>
                )}

                {comparisonData && (
                  <>
                    <div className="modifier-manager-table-wrapper">
                      <table className="modifier-manager-table">
                        <caption>Tier and threshold differences</caption>
                        <thead>
                          <tr>
                            <th scope="col">Item</th>
                            <th scope="col">Before</th>
                            <th scope="col">After</th>
                          </tr>
                        </thead>
                        <tbody>
                          {comparisonData.differences.map((difference, index) => (
                            <tr key={difference.key ?? index}>
                              <th scope="row">
                                {difference.label ??
                                  difference.field ??
                                  "Change"}
                              </th>
                              <td>
                                {difference.before ??
                                  difference.previous ??
                                  "—"}
                              </td>
                              <td>
                                {difference.after ??
                                  difference.target ??
                                  "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <dl>
                      <dt>Estimated economic impact</dt>
                      <dd>
                        {comparisonData.estimatedEconomicImpact ?? "Not supplied"}
                      </dd>
                      <dt>Affected positions</dt>
                      <dd>
                        {comparisonData.affectedPositionCount ?? "Not supplied"}
                      </dd>
                      <dt>Effective-date compatibility</dt>
                      <dd>
                        {comparisonData.effectiveDateCompatibility
                          ? (COMPAT_LABELS[comparisonData.effectiveDateCompatibility]?.label
                              ?? comparisonData.effectiveDateCompatibility)
                          : 'Not assessed'}
                      </dd>
                      <dt>Spine compatibility</dt>
                      <dd>
                        {comparisonData.spineCompatibility ?? "Not supplied"}
                      </dd>
                    </dl>
                  </>
                )}
              </div>
            </section>
          )}

          {rateTableRef.otherPlanCount > 0 && (
            <div className="modifier-manager-info">
              <p>
                This rate table is used by{" "}
                {rateTableRef.otherPlanCount} other{" "}
                {rateTableRef.otherPlanCount === 1 ? "plan" : "plans"}.
                Consider a Change Set to update all affected plans
                together.
              </p>
              {!readOnly && (
                <button
                  type="button"
                  className="secondary-button"
                  onClick={onStartChangeSet}
                >
                  Start a Change Set
                </button>
              )}
            </div>
          )}
        </div>

        {!readOnly && (
          <footer className="modifier-manager-footer">
            {!approvedWorkflow && (
              <button
                type="button"
                className="secondary-button"
                onClick={() => setDrawerOpen(true)}
              >
                Change rate table
              </button>
            )}

            {!approvedWorkflow &&
              targetVersion &&
              comparisonViewed && (
                <button
                  type="button"
                  className="primary-button"
                  onClick={() => onVersionUpdate(targetVersion)}
                >
                  Use {targetVersion} in this draft
                </button>
              )}

            {approvedWorkflow &&
              targetVersion &&
              comparisonViewed && (
                <button
                  type="button"
                  className="primary-button"
                  onClick={onStartAmendment}
                >
                  Start amendment using {targetVersion}
                </button>
              )}
          </footer>
        )}
      </section>

      <RateTableLibraryDrawer
        open={drawerOpen}
        currentRef={{
          rateTableId: rateTableRef.rateTableId,
          rateTableName: rateTableRef.rateTableName,
          versionLabel: rateTableRef.versionLabel,
        }}
        planSpine={planSpine}
        onSelect={handleDrawerSelect}
        onClose={() => setDrawerOpen(false)}
      />
    </>
  );
}
